import Phaser from 'phaser';
import { Entity, EntityConfig } from './Entity';
import { COLORS, GAME } from '../constants';
import { RenderSystem } from '../systems/RenderSystem';
import { AudioRouter } from '../systems/AudioRouter';

// ============================================
// Base Entity (Village to defend)
// ============================================

const VILLAGER_KEYS = ['villager-b1', 'villager-b2'] as const;
const VILLAGER_SCALE = 0.2;

// Area in front of hut (relative to base center). Wider horizontal range; villagers hop within this box.
const VILLAGER_AREA = {
  minX: -90,
  maxX: 90,
  minY: 50,
  maxY: 130,
};

const VILLAGER_SPEED_MIN = 18;
const VILLAGER_SPEED_MAX = 42;

// Idle 2/3 of the time, move 1/3. Durations in ms.
const VILLAGER_IDLE_DURATION_MIN = 2000;
const VILLAGER_IDLE_DURATION_MAX = 4000;
const VILLAGER_MOVE_DURATION_MIN = 800;
const VILLAGER_MOVE_DURATION_MAX = 1600;

// Hopping: vertical bounce while moving (pixels and rad/s).
const VILLAGER_HOP_HEIGHT = 6;
const VILLAGER_HOP_SPEED = 9;

// Blood explosion: spray in all directions with velocity, gravity pulls to ground (bottom of sprite), then fade
const BLOOD_COLORS = [0x8b0000, 0xa52a2a, 0x660000, 0x4a0000];
const BLOOD_BLOB_COUNT = 40;
const BLOOD_SPEED_MIN = 150;
const BLOOD_SPEED_MAX = 400;
const BLOOD_GRAVITY = 420;
const BLOOD_GROUND_Y = 0; // container origin = villager feet = ground; blood cannot go below this
const BLOOD_GROUND_FRICTION = 0.35;
const BLOOD_SETTLE_THEN_FADE_MS = 400;
const BLOOD_FADE_DURATION = 2500;

const VILLAGER_DEATH_SCREAM_KEYS = ['sfx-scream-01', 'sfx-scream-02', 'sfx-scream-03', 'sfx-scream-04', 'sfx-scream-05'] as const;
const VILLAGER_DEATH_EXPLOSION_DELAY_MS = 250;

// Small shadow under each villager (layered ellipses like Player/Monster)
const VILLAGER_SHADOW_LAYERS = [
  { width: 28, height: 9, alpha: 0.06 },
  { width: 24, height: 8, alpha: 0.08 },
  { width: 22, height: 7, alpha: 0.1 },
  { width: 20, height: 6.5, alpha: 0.12 },
  { width: 18, height: 6, alpha: 0.14 },
  { width: 14, height: 5, alpha: 0.18 },
];

type VillagerState = 'idle' | 'moving';

interface VillagerSprite {
  container: Phaser.GameObjects.Container;
  image: Phaser.GameObjects.Image;
  localX: number;
  localY: number;
  vx: number;
  vy: number;
  state: VillagerState;
  nextSwitchTime: number;
  hopPhase: number;
}

interface BloodBlob {
  blob: Phaser.GameObjects.Ellipse;
  vx: number;
  vy: number;
  settled: boolean;
}

interface BloodSim {
  container: Phaser.GameObjects.Container;
  blobs: BloodBlob[];
}

export interface BaseConfig extends Omit<EntityConfig, 'type'> {
  startingVillagers?: number;
}

export class Base extends Entity {
  private villagers: number;
  private maxVillagers: number;

  // Visuals
  private body: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Image;
  private villagerSprites: VillagerSprite[] = [];
  private bloodSims: BloodSim[] = [];
  private useImage: boolean = false;

  // Events
  public onVillagersChanged?: (remaining: number) => void;
  public onDestroyed?: () => void;

  constructor(scene: Phaser.Scene, config: BaseConfig) {
    super(scene, { ...config, type: 'base' });

    this.maxVillagers = config.startingVillagers ?? GAME.STARTING_VILLAGERS;
    this.villagers = this.maxVillagers;

    if (scene.textures.exists('village-center')) {
      this.useImage = true;
      const img = scene.add.image(0, 0, 'village-center');
      img.setScale(0.25);
      this.body = img;
    } else {
      const rect = scene.add.rectangle(0, 0, GAME.BASE_WIDTH, GAME.BASE_HEIGHT, COLORS.BASE);
      rect.setStrokeStyle(3, 0xffffff);
      this.body = rect;
    }

    this.container.add(this.body);
    this.buildVillagerSprites();

    this.updateDepth();
  }

  /** Build villager sprites from scratch (used only on init). */
  private buildVillagerSprites(): void {
    this.villagerSprites.forEach(v => v.container.destroy());
    this.villagerSprites = [];
    for (let i = 0; i < this.villagers; i++) {
      this.villagerSprites.push(this.createOneVillagerSprite());
    }
  }

  /** Create a single villager sprite at a random position in the area. */
  private createOneVillagerSprite(): VillagerSprite {
    const key1 = this.scene.textures.exists('villager-b1') ? 'villager-b1' : null;
    const key2 = this.scene.textures.exists('villager-b2') ? 'villager-b2' : null;
    const keys = [key1, key2].filter(Boolean) as string[];
    const key = keys[Math.floor(Math.random() * keys.length)] ?? 'villager-b1';

    const { minX, maxX, minY, maxY } = VILLAGER_AREA;
    const localX = minX + Math.random() * (maxX - minX);
    const localY = minY + Math.random() * (maxY - minY);
    const worldX = this.x + localX;
    const worldY = this.y + localY;

    const container = this.scene.add.container(worldX, worldY);
    container.setScrollFactor(1);

    const shadow = this.scene.add.container(0, -9);
    VILLAGER_SHADOW_LAYERS.forEach(layer => {
      const ellipse = this.scene.add.ellipse(0, 0, layer.width, layer.height, 0x000000, layer.alpha);
      shadow.add(ellipse);
    });
    container.add(shadow);

    const img = this.scene.add.image(0, 0, key);
    img.setScale(VILLAGER_SCALE);
    img.setOrigin(0.5, 1);
    container.add(img);

    const angle = Math.random() * Math.PI * 2;
    const speed = VILLAGER_SPEED_MIN + Math.random() * (VILLAGER_SPEED_MAX - VILLAGER_SPEED_MIN);
    const vx = Math.cos(angle) * speed;
    const vy = Math.sin(angle) * speed;

    const now = this.scene.time.now;
    const idleDuration = VILLAGER_IDLE_DURATION_MIN + Math.random() * (VILLAGER_IDLE_DURATION_MAX - VILLAGER_IDLE_DURATION_MIN);
    const startIdle = Math.random() < 2 / 3;

    return {
      container,
      image: img,
      localX,
      localY,
      vx,
      vy,
      state: startIdle ? 'idle' : 'moving',
      nextSwitchTime: now + (startIdle ? idleDuration : VILLAGER_MOVE_DURATION_MIN + Math.random() * (VILLAGER_MOVE_DURATION_MAX - VILLAGER_MOVE_DURATION_MIN)),
      hopPhase: Math.random() * Math.PI * 2,
    };
  }

  /** Remove N villager sprites (from the end). If spawnBlood, spawn blood and play death SFX at each. */
  private removeVillagerSprites(count: number, spawnBlood: boolean): void {
    const toRemove = this.villagerSprites.splice(this.villagerSprites.length - count, count);
    toRemove.forEach(v => {
      if (spawnBlood) {
        this.spawnBloodExplosion(this.x + v.localX, this.y + v.localY);
        const screamKey = VILLAGER_DEATH_SCREAM_KEYS[Math.floor(Math.random() * VILLAGER_DEATH_SCREAM_KEYS.length)];
        AudioRouter.playSfx(screamKey);
        this.scene.time.delayedCall(VILLAGER_DEATH_EXPLOSION_DELAY_MS, () => {
          AudioRouter.playSfx('sfx-explosion');
        });
      }
      v.container.destroy();
    });
  }

  /** Add N new villager sprites at random positions; existing villagers keep their positions. */
  private addVillagerSprites(count: number): void {
    for (let i = 0; i < count; i++) {
      this.villagerSprites.push(this.createOneVillagerSprite());
    }
  }

  /** Bottom of base visual for depth sorting */
  protected getDepthY(): number {
    return Phaser.Display.Bounds.GetBottom(this.body);
  }

  update(delta: number): void {
    const dt = delta / 1000;
    const now = this.scene.time.now;
    const { minX, maxX, minY, maxY } = VILLAGER_AREA;

    for (const v of this.villagerSprites) {
      // State switch: idle ~2/3 of the time, moving ~1/3
      if (now >= v.nextSwitchTime) {
        if (v.state === 'idle') {
          v.state = 'moving';
          const moveDuration = VILLAGER_MOVE_DURATION_MIN + Math.random() * (VILLAGER_MOVE_DURATION_MAX - VILLAGER_MOVE_DURATION_MIN);
          v.nextSwitchTime = now + moveDuration;
          // New random direction when starting to move
          const angle = Math.random() * Math.PI * 2;
          const speed = VILLAGER_SPEED_MIN + Math.random() * (VILLAGER_SPEED_MAX - VILLAGER_SPEED_MIN);
          v.vx = Math.cos(angle) * speed;
          v.vy = Math.sin(angle) * speed;
        } else {
          v.state = 'idle';
          const idleDuration = VILLAGER_IDLE_DURATION_MIN + Math.random() * (VILLAGER_IDLE_DURATION_MAX - VILLAGER_IDLE_DURATION_MIN);
          v.nextSwitchTime = now + idleDuration;
        }
      }

      if (v.state === 'moving') {
        v.localX += v.vx * dt;
        v.localY += v.vy * dt;

        // Bounce off edges (confined area in front of hut)
        if (v.localX <= minX) {
          v.localX = minX;
          v.vx = Math.abs(v.vx);
        } else if (v.localX >= maxX) {
          v.localX = maxX;
          v.vx = -Math.abs(v.vx);
        }
        if (v.localY <= minY) {
          v.localY = minY;
          v.vy = Math.abs(v.vy);
        } else if (v.localY >= maxY) {
          v.localY = maxY;
          v.vy = -Math.abs(v.vy);
        }

        // Cute hopping: vertical bounce (sine so they bob up and down)
        v.hopPhase += dt * VILLAGER_HOP_SPEED;
        const hopOffset = Math.abs(Math.sin(v.hopPhase)) * VILLAGER_HOP_HEIGHT;
        v.image.setY(-hopOffset);
        v.image.setFlipX(v.vx < 0);
      } else {
        v.image.setY(0);
      }

      const worldX = this.x + v.localX;
      const worldY = this.y + v.localY;
      v.container.setPosition(worldX, worldY);

      // Same Y-based depth as characters and monsters (lower on screen = in front)
      v.container.setDepth(RenderSystem.calculateUnitDepth(worldY));
    }

    // Blood physics: gravity, ground clamp, then fade when all settled
    const dtSec = dt;
    for (let i = this.bloodSims.length - 1; i >= 0; i--) {
      const sim = this.bloodSims[i];
      for (const b of sim.blobs) {
        if (b.settled) continue;
        let nx = b.blob.x + b.vx * dtSec;
        let ny = b.blob.y + b.vy * dtSec;
        let nvx = b.vx;
        let nvy = b.vy + BLOOD_GRAVITY * dtSec;
        if (ny > BLOOD_GROUND_Y) {
          ny = BLOOD_GROUND_Y;
          nvy = 0;
          nvx *= BLOOD_GROUND_FRICTION;
          if (Math.abs(nvx) < 2) nvx = 0;
          b.settled = true;
        }
        b.blob.setPosition(nx, ny);
        b.vx = nvx;
        b.vy = nvy;
      }
      const allSettled = sim.blobs.every(b => b.settled);
      if (allSettled) {
        this.bloodSims.splice(i, 1);
        this.scene.time.delayedCall(BLOOD_SETTLE_THEN_FADE_MS, () => {
          if (!sim.container.scene) return;
          this.scene.tweens.add({
            targets: sim.container,
            alpha: 0,
            duration: BLOOD_FADE_DURATION,
            ease: 'Linear',
            onComplete: () => sim.container.destroy(),
          });
        });
      }
    }
  }

  monsterArrived(damage: number = 1): void {
    this.villagers = Math.max(0, this.villagers - damage);
    this.updateDisplay(true);

    if (this.villagers <= 0) {
      this.onDestroyed?.();
    }
  }

  syncVillagers(count: number): void {
    const previousCount = this.villagers;
    this.villagers = count;
    // Spawn blood when count decreases (damage path uses SessionManager -> syncVillagers, not monsterArrived)
    this.updateDisplay(count < previousCount);

    if (this.villagers <= 0) {
      this.onDestroyed?.();
    }
  }

  /** Sync sprite count to this.villagers. When villagers die (from damage), pass spawnBlood=true. */
  private updateDisplay(spawnBloodOnDecrease: boolean = false): void {
    const delta = this.villagerSprites.length - this.villagers;
    if (delta > 0) {
      this.removeVillagerSprites(delta, spawnBloodOnDecrease);
    } else if (delta < 0) {
      this.addVillagerSprites(-delta);
    }
  }

  /** Spawn blood spray in all directions with velocity; gravity pulls to ground (villager feet), then fade. */
  private spawnBloodExplosion(worldX: number, worldY: number): void {
    const container = this.scene.add.container(worldX, worldY);
    container.setScrollFactor(1);
    container.setDepth(RenderSystem.calculateUnitDepth(worldY));

    const blobs: BloodBlob[] = [];

    for (let i = 0; i < BLOOD_BLOB_COUNT; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = BLOOD_SPEED_MIN + Math.random() * (BLOOD_SPEED_MAX - BLOOD_SPEED_MIN);
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed;

      const color = BLOOD_COLORS[Math.floor(Math.random() * BLOOD_COLORS.length)];
      const w = 5 + Math.random() * 12;
      const h = 3 + Math.random() * 8;
      const blob = this.scene.add.ellipse(0, 0, w, h, color, 0.9 + Math.random() * 0.1);
      container.add(blob);

      blobs.push({ blob, vx, vy, settled: false });
    }

    this.bloodSims.push({ container, blobs });
  }

  getVillagers(): number { return this.villagers; }
  getMaxVillagers(): number { return this.maxVillagers; }
  isDestroyed(): boolean { return this.villagers <= 0; }

  addVillagers(count: number): void {
    this.villagers = Math.min(this.maxVillagers, this.villagers + count);
    this.updateDisplay();
    this.onVillagersChanged?.(this.villagers);
  }

  destroy(): void {
    this.villagerSprites.forEach(v => v.container.destroy());
    this.villagerSprites = [];
    this.bloodSims.forEach(sim => sim.container.destroy());
    this.bloodSims = [];
    super.destroy();
  }
}
